import Image from 'next/image';
import React, { useCallback, useState, useEffect } from 'react';
import { Rating } from 'react-simple-star-rating';
import getTimeAgo from '../../../utils/getDateTime';
import DetailedReviewModal from '../../../shared-components/testimonials/detailedReviewModal';
import ReviewFilters from './ReviewFilters';
import { LoadingSpinner } from '../../../shared-components/LoadingSpinner/LoadingSpinner';
import Pagination from '../../../shared-components/Pagination/Pagination';
import AddReviewModal from './AddReviewModal';
import { useAuthContext } from '../../../utils/context/authContext';
import scrollToElement from '../../../utils/scrollIntoView';
import { Review } from '../../../pages/[slug]/[slug2]';
import NotificationModal, {
  EModalType
} from '../../../shared-components/NotificationModal/NotificationModal';
import { useRouter } from 'next/router';
import client from '../../../apollo-client';
import { BOOKING_DATA } from '../../../api/fetch_user_details';

interface ReviewsSectionProps {
  rating: number;
  totalCount: number;
  reviews: Review[];
  productName: string;
  setReviews: React.Dispatch<React.SetStateAction<Review[]>>;
  id: number | string;
  currentPage: number;
  positiveReview?: Review | undefined;
  secondMostPositiveReview?: Review | undefined;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  setSelectedRating: React.Dispatch<React.SetStateAction<string>>;
  selectedRating: string;
  reviewsCount: number;
  totalReviewsCount?: number;
  fetchReviews: () => Promise<void>;
}
interface ReviewCardProps {
  review: Review;
  handleSeeMoreClick: (review: Review) => void;
  isValidImageUrl: (url: string) => boolean;
  user: any;
  productName: string;
}

const ReviewCard: React.FC<ReviewCardProps> = ({
  review,
  handleSeeMoreClick,
  isValidImageUrl,
  user,
  productName
}) => {
  const userInitial = review.name ? review.name.charAt(0).toUpperCase() : '?';

  return (
    <div className="flex flex-col w-full gap-4 mb-4">
      <div className="flex items-center">
        {review.name ? (
          <div className="w-[38px] h-[37px] text-lg rounded-full bg-primary text-white flex text-center items-center justify-center font-semibold">
            {userInitial}
          </div>
        ) : (
          <Image
            src="/default-profile-icon.svg"
            alt="profile-icon"
            width={38}
            height={38}
            className="rounded-full"
          />
        )}

        <div className="ml-4 flex flex-col font-bold w-full">
          <div className="flex justify-between w-full relative">
            <span className="text-[14px] md:text-[16px] font-semibold">
              {review?.name || review?.traveller || 'Anonymous User'}
            </span>
          </div>

          <span className="text-sm md:text-base flex gap-2 items-center font-semibold">
            <Rating
              initialValue={Number(review.rating)}
              disableFillHover={true}
              allowFraction={true}
              fillColor="orange"
              emptyColor="#9B9B9B"
              readonly={true}
              SVGstyle={{ display: 'inline-block' }}
              size={15}
            />
            {review.created_at && getTimeAgo(review.created_at)}
          </span>
        </div>
      </div>

      <span className="text-justify text-sm xl:text-[16px] font-light pr-2 overflow-hidden">
        {review?.review?.length > 300 ? (
          <>
            {review.review.slice(0, 300)}
            <span>....</span>
            <span
              className="font-semibold cursor-pointer"
              onClick={() => handleSeeMoreClick(review)}
            >
              see more
            </span>
          </>
        ) : (
          review.review
        )}
      </span>
    </div>
  );
};

const ReviewsSection: React.FC<ReviewsSectionProps> = ({
  rating,
  totalCount,
  reviews,
  setReviews,
  id,
  positiveReview,
  secondMostPositiveReview,
  currentPage,
  setCurrentPage,
  setSelectedRating,
  selectedRating,
  totalReviewsCount,
  reviewsCount,
  productName,
  fetchReviews
}) => {
  const [showErrorModal, setShowErrorModal] = useState(false);
  const { user }: any = useAuthContext();
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const router = useRouter();
  const toggleModal = useCallback(() => {
    setShowErrorModal(!showErrorModal);
  }, [showErrorModal]);

  const [selectedReviewData, setSelectedReviewData] = useState<Review | null>(
    null
  );
  const [selectedReview, setSelectedReview] = useState<Review>();
  const [showModal, setShowModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const handleSearchChange = (event: any) => {
    setSearchQuery(event.target.value);
  };
  const handleSeeMoreClick = (review: Review) => {
    setSelectedReview(review);
    setShowModal(true);
  };
  const checkBookingValidity = async (bookingId: string) => {
    try {
      const response = await client.query({
        query: BOOKING_DATA,
        variables: { bookingId }
      });

      const booking = response.data?.boatnew_booking?.[0];
      return !!booking?.customer?.email && !!booking?.product?.id;
    } catch {
      return false; // Silent failure - UI handles messaging
    }
  };
  const openModalHandler = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const bookingId = urlParams.get('id');

    if (bookingId) {
      const isValidBooking = await checkBookingValidity(bookingId);
      if (isValidBooking) {
        setIsReviewModalOpen(true);
        return;
      }
    }
    setIsReviewModalOpen(true);
  };

  // Function to validate image URLs
  const isValidImageUrl = (url: string) => {
    if (!url) return false;
    if (typeof url !== 'string') return false;

    // Check if the URL is a Firebase Storage URL or Google Photos URL
    const firebaseUrlPattern =
      /^https:\/\/firebasestorage\.googleapis\.com\/v0\/b\//;
    const googlePhotoUrlPattern = /^https:\/\/lh3\.googleusercontent\.com\//;

    return firebaseUrlPattern.test(url) || googlePhotoUrlPattern.test(url);
  };
  const filteredReviews = reviews?.filter((review: any) => {
    const matchesSearch =
      review?.review?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      searchQuery.trim() === '';
    return matchesSearch;
  });
  const [loader, setLoader] = React.useState(false);

  const handleRatingChange = (event: any) => {
    const newSelectedRating = event.target.value;
    setSelectedRating(newSelectedRating);
    setSearchQuery('');
    setCurrentPage(1);
  };

  useEffect(() => {
    setCurrentPage(1);
    setSelectedRating('');
  }, [id]);
  const itemsPerPage = 5;
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    scrollToElement('reviews');
  };
  const totalPages = (reviews && Math.ceil(reviewsCount / itemsPerPage)) || 0;

  return (
    <div>
      {/* Header Section */}
      <div className="flex my-2 gap-4">
        <div className="flex xxsm:justify-between msm:justify-normal xxsm:gap-0 msm:gap-8 mt-4 mb-3 font-semibold w-full">
          <div className="flex gap-2">
            <h2
              className="md:pl-0 text-lg lg:text-xl font-semibold"
              id="reviews"
            >
              Reviews
            </h2>
            {rating > 0 && (
              <span className="flex items-center gap-1">
                <div className="flex">
                  {[...Array(5)].map((_, index) => (
                    <Image
                      key={index}
                      src="/homestar.svg"
                      width={15}
                      height={15}
                      alt="rating star"
                    />
                  ))}
                </div>
                <span className="font-semibold">{rating.toFixed(1)}</span> (
                {totalCount})
              </span>
            )}
          </div>
          <button
            onClick={event => {
              event.stopPropagation();
              openModalHandler();
            }}
            className="flex items-center justify-center rounded-full border-gray-200 bg-primary w-8 h-8 text-white"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="15"
              height="15"
              viewBox="0 0 15 15"
              fill="none"
            >
              <path
                d="M13.9286 8.57143H8.57143V13.9286C8.57143 14.2127 8.45855 14.4853 8.25761 14.6862C8.05668 14.8871 7.78416 15 7.5 15C7.21584 15 6.94332 14.8871 6.74239 14.6862C6.54145 14.4853 6.42857 14.2127 6.42857 13.9286V8.57143H1.07143C0.787268 8.57143 0.514746 8.45855 0.313814 8.25762C0.112883 8.05668 0 7.78416 0 7.5C0 7.21584 0.112883 6.94332 0.313814 6.74239C0.514746 6.54145 0.787268 6.42857 1.07143 6.42857H6.42857V1.07143C6.42857 0.787268 6.54145 0.514746 6.74239 0.313814C6.94332 0.112882 7.21584 0 7.5 0C7.78416 0 8.05668 0.112882 8.25761 0.313814C8.45855 0.514746 8.57143 0.787268 8.57143 1.07143V6.42857H13.9286C14.2127 6.42857 14.4853 6.54145 14.6862 6.74239C14.8871 6.94332 15 7.21584 15 7.5C15 7.78416 14.8871 8.05668 14.6862 8.25762C14.4853 8.45855 14.2127 8.57143 13.9286 8.57143Z"
                fill="white"
              />
            </svg>
          </button>
        </div>
      </div>
      {totalReviewsCount !== undefined && totalReviewsCount > 0 && (
        <>
          <div className="flex align-center justify-start mb-4">
            <h2 className="text-[16px] md:text-[16px] xl:text-[18px] font-semibold my-2">
              Most Helpful Positive Reviews
            </h2>
          </div>
          {positiveReview === null ||
            (positiveReview === undefined && (
              <div className="text-start font-light mb-2">
                No Positive reviews found.
              </div>
            ))}
          <div className="w-full max-h-[800px] flex flex-col md:flex-row">
            <div className="md:w-1/2 flex-1 flex flex-col">
              <div
                className={`${
                  secondMostPositiveReview === null ||
                  secondMostPositiveReview === undefined ||
                  (Array.isArray(secondMostPositiveReview) &&
                    secondMostPositiveReview.length === 0)
                    ? ''
                    : 'border-r border-gray-300'
                }`}
              >
                <div className="w-full flex-wrap align-middle md:pr-[9%] font-bold gap-4">
                  {positiveReview && (
                    <ReviewCard
                      key={`${positiveReview.name}-positive`}
                      review={positiveReview}
                      handleSeeMoreClick={handleSeeMoreClick}
                      isValidImageUrl={isValidImageUrl}
                      user={user}
                      productName={productName}
                    />
                  )}
                </div>
              </div>
            </div>
            <div className="md:w-1/2 flex-1 flex flex-col">
              <div>
                <div className="w-full flex-wrap align-middle md:pl-[9%] font-bold gap-4">
                  {secondMostPositiveReview && (
                    <ReviewCard
                      key={`${secondMostPositiveReview.name}-critical`}
                      review={secondMostPositiveReview}
                      handleSeeMoreClick={handleSeeMoreClick}
                      isValidImageUrl={isValidImageUrl}
                      user={user}
                      productName={productName}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {showModal && selectedReview && (
        <DetailedReviewModal
          setShowModal={setShowModal}
          data={selectedReview}
        />
      )}
      {showErrorModal && (
        <NotificationModal
          toggleModal={toggleModal}
          modalType={EModalType.ERROR}
          message="To access this feature, please log in to your account first."
          buttonText="Sign in"
          buttonHandler={() => router.push('/signin')}
        />
      )}
      <div className="w-full flex">
        <div className="flex-1 flex flex-col">
          {totalReviewsCount !== undefined && totalReviewsCount > 0 && (
            <ReviewFilters
              searchQuery={searchQuery}
              handleRatingChange={handleRatingChange}
              handleSearchChange={handleSearchChange}
              selectedRating={selectedRating}
            />
          )}
          {loader ? (
            <div className="flex justify-center items-center h-10">
              <LoadingSpinner />
            </div>
          ) : (
            <div className="flex-wrap align-middle font-bold gap-4">
              {filteredReviews && filteredReviews.length > 0 ? (
                filteredReviews.map((review, index) => (
                  <ReviewCard
                    key={index}
                    review={review}
                    handleSeeMoreClick={handleSeeMoreClick}
                    isValidImageUrl={isValidImageUrl}
                    user={user}
                    productName={productName}
                  />
                ))
              ) : (
                <div className="text-[14px] md:text-base font-medium">
                  No Reviews found
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {totalPages > 1 && (
        <Pagination
          totalPages={totalPages}
          currentPage={currentPage}
          handlePageChange={handlePageChange}
        />
      )}

      {isReviewModalOpen && (
        <AddReviewModal
          onClose={() => setIsReviewModalOpen(false)}
          setIsModalOpen={setIsReviewModalOpen}
          id={id}
          fetchReviews={fetchReviews}
          selectedReviewData={selectedReviewData}
          setSelectedReviewData={setSelectedReviewData}
        />
      )}
    </div>
  );
};

export default ReviewsSection;
